import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Filter,
  SortDesc,
  Package,
  Eye,
  ShoppingCart,
} from 'lucide-react';
import httpClient from '../api/axios';

const Products = () => {
  // Local state for product data
  const [products, setProducts] = useState([]);

  const fetchProducts = async () => {
    try {
      const response = await httpClient.get(`/api/products`);
      const activeProducts = response.data.filter(
        (product) => product.isActive
      );

      setProducts(activeProducts);
      console.log(
        'Active products fetched successfully:',
        activeProducts
      );
    } catch (error) {
      console.log('Error fetching products:', error);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Local state for cart
  const [cart, setCart] = useState([]);

  // UI state
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [cartNotification, setCartNotification] = useState(false);
  const [cartMessage, setCartMessage] = useState('');

  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 100 },
    },
  };

  // Function to add product to cart
  const addToCart = (productToAdd) => {
    const existingItem = cart.find(
      (item) => item.id === productToAdd.id
    );
    if (existingItem) {
      setCart(
        cart.map((item) =>
          item.id === productToAdd.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      setCart([...cart, { ...productToAdd, quantity: 1 }]);
    }

    setCartMessage(`${productToAdd.name} added to cart.`);
    setCartNotification(true);

    setTimeout(() => setCartNotification(false), 2500);
  };

  const buyNow = (productToAdd) => {
    navigate(`/cart/${productToAdd?.id}/1`);
  };

  const handleViewDetails = (product) => {
    navigate(`/product/${product._id}`);
  };

  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  // Product Card Component
  const ProductCard = ({ product }) => {
    return (
      <motion.div
        variants={itemVariants}
        className='group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 ease-out hover:-translate-y-1 overflow-hidden relative'
      >
        {/* Image Section */}
        <div className='h-60 bg-gray-100 relative overflow-hidden'>
          <img
            src={product.productImg}
            alt={product.name}
            className='w-full h-full object-cover transition-transform duration-300 group-hover:scale-105'
          />
          {/* Quick View Overlay */}
          <div className='absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center'>
            <button
              onClick={() => handleViewDetails(product)}
              className='opacity-0 group-hover:opacity-100 transform -translate-y-4 group-hover:translate-y-0 transition-all duration-300'
            >
              <Eye className='text-white w-8 h-8 bg-black bg-opacity-60 p-2 rounded-full' />
            </button>
          </div>
        </div>

        {/* Product Info */}
        <div className='p-4'>
          {/* Category Tag */}
          <span className='text-xs font-medium text-gray-500 uppercase tracking-wide'>
            {product.category}
          </span>

          {/* Product Name */}
          <h3 className='mt-1 text-lg font-semibold text-gray-900 hover:text-emerald-600 transition-colors'>
            <button onClick={() => handleViewDetails(product)}>
              {product.name}
            </button>
          </h3>

          {/* Price & Stock */}
          <div className='mt-3 flex justify-between items-center'>
            <div>
              <span className='text-xl font-bold text-emerald-600'>
                Rs.{product.pricePerKg.toFixed(2)}
              </span>
              {/* <span className='text-sm text-gray-500 ml-1'>
                per kg
              </span> */}
            </div>
          </div>

          {/* Action Buttons */}
          <div className='mt-4 grid grid-cols-2 gap-2'>
            {/* Details Button */}
            <div className='mt-2'>
              <Link
                to={`/product/${product._id}`}
                className='flex items-center justify-center py-2 px-4 w-full bg-gray-100 rounded-md hover:bg-gray-200 transition-colors text-sm font-medium text-gray-700'
              >
                <Eye className='mr-2' size={16} /> View Details
              </Link>
            </div>
            <button
              onClick={() =>
                buyNow({
                  id: product._id,
                  name: product.name,
                  productImg: product.productImg,
                  pricePerKg: product.pricePerKg,
                  quantityKg: 1,
                })
              }
              className='flex items-center justify-center py-2 px-4 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-md hover:from-emerald-700 hover:to-emerald-600 transition-all text-sm font-medium'
              disabled={product.quantity === 0}
            >
              Buy Now
            </button>
          </div>
        </div>

        {/* Sale Badge */}
        {product.sale && (
          <div className='absolute top-2 right-2 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-medium'>
            SALE
          </div>
        )}

        {/* Out of Stock Overlay */}
        {product.quantity === 0 && (
          <div className='absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center'>
            <div className='bg-white px-4 py-2 rounded-lg text-red-600 font-semibold'>
              Out of Stock
            </div>
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-20'
    >
      {/* Page header */}
      <motion.div
        className='mb-8'
        initial={{ y: -20 }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 300 }}
      >
        <h1 className='text-3xl font-bold text-gray-900'>
          Our Flour Products
        </h1>
        <p className='mt-2 text-gray-600'>
          Discover our range of high-quality flours for all your
          baking needs
        </p>
      </motion.div>

      {/* Mobile filter toggle */}
      <div className='sm:hidden mb-4'>
        <button
          onClick={toggleFilter}
          className='w-full flex items-center justify-center py-2 px-4 bg-white border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50'
        >
          <Filter size={18} className='mr-2' />
          {isFilterOpen ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>

      {/* Product count */}
      <motion.div
        className='mb-6 py-2 px-4 bg-gray-100 rounded-lg text-gray-700 inline-flex items-center'
        initial={{ x: -20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <Package size={18} className='mr-2' />
        <span>
          Showing{' '}
          <span className='font-semibold'>{products.length}</span>{' '}
          products
        </span>
      </motion.div>

      <motion.div
        variants={containerVariants}
        initial='hidden'
        animate='visible'
        className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6'
      >
        {products?.map((product) => (
          <ProductCard key={product._id} product={product} />
        ))}
      </motion.div>
    </motion.div>
  );
};

export default Products;
